import { useEffect, useRef, useState } from 'react'

// Click-to-edit input.  Shows the value as text with a dashed-underline hover
// cue; click promotes it to an <input>.  Enter or blur saves via onSave;
// Esc cancels.  Save errors surface via toast (parent decides) and the value
// reverts to the original.
//
// Usage:
//   <InlineText value={m.title} onSave={(v) => saveField(m, 'title', v)} />
//   <InlineSelect value={m.status} options={STATUS_OPTS} onSave={(v) => saveField(m, 'status', v)} />
//
// Save is only fired when the value actually changes.  A pending save disables
// further edits until it resolves.  On failure, `onSave` should throw or
// return a rejected promise so the component can revert.

export function InlineText({
  value, onSave, canEdit = true, placeholder = '(none)',
  className = '', inputClassName = '', maxLength = 200
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  // Keep draft in sync when the underlying value changes externally
  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select?.()
    }
  }, [editing])

  const commit = async () => {
    const trimmed = draft.trim()
    if (trimmed === (value ?? '').trim()) { setEditing(false); return }
    setBusy(true)
    try {
      await onSave(trimmed)
      setEditing(false)
    } catch (e) {
      // Revert draft to the last-known-good value
      setDraft(value ?? '')
    } finally {
      setBusy(false)
    }
  }

  const cancel = () => {
    setDraft(value ?? '')
    setEditing(false)
  }

  if (!canEdit) {
    return <span className={className}>{value || <em className="text-gray-400">{placeholder}</em>}</span>
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        maxLength={maxLength}
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          if (e.key === 'Escape') { e.preventDefault(); cancel() }
        }}
        className={`bg-white border border-primary-400 rounded px-2 py-0.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-primary-500 ${inputClassName}`}
      />
    )
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true) }}
      title="Click to edit"
      className={`cursor-text hover:decoration-dotted hover:underline hover:decoration-primary-400 hover:decoration-1 hover:underline-offset-4 ${className}`}
    >
      {value || <em className="text-gray-400">{placeholder}</em>}
    </span>
  )
}

export function InlineSelect({
  value, options, onSave, canEdit = true,
  renderValue = null, className = ''
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const selectRef = useRef(null)

  useEffect(() => {
    if (editing && selectRef.current) selectRef.current.focus()
  }, [editing])

  const commit = async (newValue) => {
    if (newValue === value) { setEditing(false); return }
    setBusy(true)
    try {
      await onSave(newValue)
      setEditing(false)
    } catch (e) {
      // stay in edit mode so the user can pick again
    } finally {
      setBusy(false)
    }
  }

  if (!canEdit) {
    return <span className={className}>{renderValue ? renderValue(value) : value}</span>
  }

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={value ?? ''}
        disabled={busy}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => setEditing(false)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Escape') setEditing(false)
        }}
        className="bg-white border border-primary-400 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    )
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true) }}
      title="Click to change"
      className={`cursor-pointer hover:ring-2 hover:ring-primary-200 rounded ${className}`}
    >
      {renderValue ? renderValue(value) : value}
    </span>
  )
}
